<template>
  <div class="update-log">
    <div class="page-header">
      <div class="h1">更新日志</div>
      <div class="sub">我们在持续进行功能和体验升级</div>
    </div>

    <div class="log-list" v-if="logs.length > 0">
      <div class="log-row" v-for="(log, index) in logs" :key="log.version">
        <div
          class="log-left"
          :class="{
            'log-first': index === 0,
            'log-last': index === logs.length - 1,
          }"
        >
          <span
            class="log-dot"
            :style="{ '--seg-h': segH[index] + 'rem' }"
          ></span>
          <div class="log-ver">{{ log.version }}</div>
          <div class="log-date">{{ log.date }}</div>
        </div>
        <div class="log-body">
          <div class="log-card">
            <div class="log-group" v-for="(group, gi) in log.groups" :key="gi">
              <div class="group-title" v-if="group.title">
                {{ group.title }}
              </div>
              <div
                class="card-item"
                v-for="(item, ii) in group.items"
                :key="ii"
              >
                <span
                  v-if="item.type"
                  class="badge"
                  :class="'badge-' + item.type"
                  >{{ badgeText(item.type) }}</span
                >
                <span class="item-text">{{ item.text }}</span>
                <a
                  v-if="item.link"
                  :href="item.linkUrl || 'javascript:;'"
                  target="_blank"
                  class="item-link"
                  >{{ item.link }}</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="list-footer" v-if="loading || finished">
        <div v-if="loading" class="load-more">
          <span class="load-spinner"></span>
          <span>加载中...</span>
        </div>
        <div v-else-if="finished" class="load-end">没有更多了</div>
      </div>
    </div>

    <div class="log-loading" v-else>加载中...</div>

    <Foot></Foot>
  </div>
</template>

<script>
  import Foot from '@/components/Foot.vue'
  import reqApi from '@/req'

  // 日期格式转化
  function formatDate(dateStr) {
    let parts = String(dateStr || '').split('-')
    let joined = []
    for (let i = 0; i < parts.length; i++) {
      joined.push(parseInt(parts[i], 10))
    }
    return joined.join('-')
  }

  export default {
    name: 'UpdateLog',
    components: {
      Foot,
    },
    data: function () {
      return {
        logs: [],
        segH: [],
        page: 1,
        pageSize: 20,
        total: 0,
        loading: false,
        finished: false,
      }
    },
    methods: {
      // 首次加载更新日志（从第 1 页开始）
      loadLogs: function () {
        let _that = this
        _that.loading = true
        reqApi.changelog.list(
          { page: 1, pagesize: this.pageSize },
          function (err, res) {
            _that.loading = false
            if (err || !res || res.rtn !== 0 || !res.data) {
              return
            }
            _that.logs = _that.formatLogs(res.data)
            _that.total = res.total
            _that.page = 1
            _that.finished = _that.logs.length >= _that.total
            _that.$nextTick(function () {
              _that.measureSegs()
            })
          },
        )
      },

      // 加载下一页并追加到列表尾部
      loadMore: function () {
        let _that = this
        if (_that.loading || _that.finished) {
          return
        }
        let nextPage = _that.page + 1
        _that.loading = true
        reqApi.changelog.list(
          { page: nextPage, pagesize: this.pageSize },
          function (err, res) {
            _that.loading = false
            if (err || !res || res.rtn !== 0 || !res.data) {
              return
            }
            _that.logs = _that.logs.concat(_that.formatLogs(res.data))
            _that.total = res.total
            _that.page = nextPage
            _that.finished = _that.logs.length >= _that.total
            _that.$nextTick(function () {
              _that.measureSegs()
            })
          },
        )
      },

      // 接口原始数据转成页面渲染用的结构（版本号、日期、内容分组）
      formatLogs: function (data) {
        let _that = this
        return data.map(function (item) {
          return {
            version: String(item.version || ''),
            date: formatDate(item.release_date),
            groups: _that.parseContent(item.content),
          }
        })
      },

      // 解析接口的 content 文本为分组结构。
      // 兼容格式：# 组标题、[new][add][opt][optimize][fix][delete] 标签、新增：文本、纯文本、
      // 以及内嵌链接 [文字](url)
      parseContent: function (content) {
        if (!content || typeof content !== 'string') {
          return []
        }
        let groups = []
        let cur = null
        content.split(/\n/).forEach(function (lineStr) {
          const line = lineStr
            .replace(/^[-*]\s*/, '')
            .replace(/^>\s*/, '')
            .trim()
          if (!line) {
            return
          }
          if (/^#+\s*/.test(line)) {
            cur = { title: line.replace(/^#+\s*/, '').trim(), items: [] }
            groups.push(cur)
            return
          }
          const tag = line.match(/^\[(\w+)\]\s*[:：]?\s*/i)
          const zh = line.match(/^(新增|新功能|优化|修复)[:：\s]+(.+)/)
          let type = null
          let text = line
          if (tag) {
            const map = {
              new: 'new',
              add: 'new',
              opt: 'optimize',
              optimize: 'optimize',
              fix: 'fix',
              bug: 'fix',
              del: 'delete',
              delete: 'delete',
            }
            type = map[tag[1].toLowerCase()] || null
            text = line.replace(tag[0], '').trim()
            if (!type && /^\[[^\]]+\]/.test(text)) {
              text = text.replace(/^\[[^\]]+\]\s*/, '')
            }
          } else if (zh) {
            const map = {
              新增: 'new',
              新功能: 'new',
              优化: 'optimize',
              修复: 'fix',
            }
            type = map[zh[1]]
            text = zh[2].trim()
          }
          if (!cur) {
            cur = { title: '', items: [] }
            groups.push(cur)
          }
          const item = { type: type, text: text }
          const link = text.match(/\[([^\]]+)\]\(([^)]+)\)/)
          if (link) {
            item.link = link[1]
            item.linkUrl = link[2]
            item.text = text.replace(link[0], '').trim()
          }
          cur.items.push(item)
        })
        return groups
      },

      // 页面滚动监听：距底部小于 300px 时触发加载下一页（防抖 200ms）
      onScroll: function () {
        let _that = this
        clearTimeout(this._scrollTimer)
        this._scrollTimer = setTimeout(function () {
          let el = document.documentElement
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 300) {
            _that.loadMore()
          }
        }, 200)
      },
      // 计算相邻圆点的垂直间距并换算成 rem 保存，
      // 竖线长度由该值驱动，窗口缩放时线长跟随 rem 同步缩放
      measureSegs: function () {
        let dots = this.$el.querySelectorAll('.log-dot')
        if (dots.length === 0) {
          return
        }
        let htmlFs = parseFloat(
          getComputedStyle(document.documentElement).fontSize,
        )
        if (!htmlFs) {
          return
        }
        let tops = []
        dots.forEach(function (dot) {
          let r = dot.getBoundingClientRect()
          tops.push(r.top + r.height / 2)
        })
        let segs = []
        for (let i = 0; i < tops.length; i++) {
          if (i < tops.length - 1) {
            segs.push(
              Math.round(((tops[i + 1] - tops[i]) / htmlFs) * 1000) / 1000,
            )
          } else {
            segs.push(0)
          }
        }
        this.segH = segs
      },

      // 徽章类型转中文文案（new/optimize/fix/delete 对应 新增/优化/修复/删除）
      badgeText: function (type) {
        const map = {
          new: '新增',
          optimize: '优化',
          fix: '修复',
          delete: '删除',
        }
        return map[type] || type
      },
    },
    mounted: function () {
      this.loadLogs()
      window.addEventListener('scroll', this.onScroll)
    },
    beforeDestroy: function () {
      window.removeEventListener('scroll', this.onScroll)
    },
  }
</script>

<style scoped lang="less">
  .update-log {
    min-height: 100vh;
    background-color: #ffffff;
    background-image: url('../../assets/v5/update_bg.png');
    background-repeat: no-repeat;
    background-position: top center;
    background-size: 100% auto;
    font-family: 'PingFang SC', 'Helvetica Neue', 'Microsoft Yahei', sans-serif;

    .page-header {
      padding-top: 96px;
      text-align: center;

      .h1 {
        font-size: 44px;
        font-weight: 700;
        color: #1d1f24;
        line-height: 60px;
        letter-spacing: 4px;
      }
      .sub {
        margin-top: 20px;
        color: var(--08, #1d1f24);
        text-align: center;
        font-family: 'Source Han Sans CN';
        font-size: 20px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
      }
    }

    .log-list {
      position: relative;
      width: 1280px;
      margin: 0 auto;
      padding: 64px 0 88px;
      transform: translateX(-100px);
    }
    .log-row {
      display: flex;
    }
    .log-row + .log-row {
      margin-top: 64px;
    }

    .log-left {
      position: relative;
      width: 300px;
      padding: 8px 51px 0 0;
      box-sizing: border-box;
      text-align: right;

      .log-dot {
        position: absolute;
        right: 15px;
        top: 17px;
        z-index: 1;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        border: 3px solid #3772ff;
        background: #ffffff;
        box-sizing: content-box;
      }
      .log-dot::before {
        content: '';
        position: absolute;
        left: 4px;
        top: 13px;
        z-index: -1;
        width: 2px;
        height: calc(var(--seg-h, 0rem) - 0.08333rem);
        background: #dde4f2;
      }
      .log-dot::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 4px;
        height: 4px;
        margin: -2px 0 0 -2px;
        border-radius: 50%;
        background: #3772ff;
      }
      .log-ver {
        font-size: 24px;
        font-weight: 700;
        color: #1d1f24;
        line-height: 34px;
      }
      .log-date {
        margin-top: 4px;
        font-size: 14px;
        color: #9090a2;
        line-height: 20px;
      }
    }

    .log-body {
      flex: 1;
      padding-left: 35px;
    }
    .log-card {
      padding: 32px 40px 36px 40px;
      background: #fff;
      border-radius: 20px;
      box-shadow: 0 2px 30px 0 #edf2ff;

      .log-group {
        .group-title {
          font-size: 20px;
          font-weight: 500;
          color: #1d1f24;
          line-height: 28px;
        }
      }
      .log-group + .log-group {
        margin-top: 24px;
      }
      .card-item {
        display: flex;
        align-items: flex-start;
        margin-top: 16px;
        font-size: 16px;
        color: #33384f;
        line-height: 26px;

        .badge {
          flex-shrink: 0;
          margin-right: 12px;
          margin-top: 2px;
          padding: 3px 8px;
          border-radius: 20px;
          font-size: 12px;
          line-height: 16px;
        }
        .badge-new {
          color: #22a15d;
          background: #e6f7ec;
        }
        .badge-optimize {
          color: #3772ff;
          background: #e8effe;
        }
        .badge-fix {
          color: #e8862e;
          background: #fdefe3;
        }
        .badge-delete {
          color: #e35c4a;
          background: #fdeeec;
        }
        .item-text {
          flex: 0 1 auto;
        }
        .item-link {
          flex-shrink: 0;
          margin-left: 6px;
          font-size: 16px;
          color: #3772ff;
          text-decoration: none;
          cursor: pointer;
          white-space: nowrap;
        }
      }
    }
    .log-loading {
      padding: 120px 0 160px;
      text-align: center;
      font-size: 16px;
      color: #9090a2;
    }

    .list-footer {
      padding-top: 24px;
      text-align: center;
      font-size: 14px;
      color: #9090a2;

      .load-more {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .load-spinner {
        display: inline-block;
        width: 16px;
        height: 16px;
        margin-right: 8px;
        border: 2px solid #dde4f2;
        border-top-color: #3772ff;
        border-radius: 50%;
        animation: load-spin 0.8s linear infinite;
      }
      .load-end {
        padding: 8px 0 16px;
      }
    }
    @keyframes load-spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  }
</style>
